import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { makeApi, URLS } from "Utils/apiURL";
import { AccountLogo } from "./AccountLogo";
import { timeAgo } from "./signalMeta";
import "./accountScore.scss";

// Account scores: 0 to 100 per account, read from its recent signals for one seller profile.

const ok = res => !!res && res.status >= 200 && res.status < 300;

/** {accountId: score item} for these accounts' latest stored scores; {} when the server can't say. */
export const fetchScores = async ids => {
  if (!ids || !ids.length) return {};
  const res = await makeApi({ url: URLS.signalsScores, method: "GET", params: { accountIds: ids.join(",") } });
  const scores = (ok(res) && res.data && res.data.scores) || [];
  return scores.reduce((out, s) => (s && s.accountId ? { ...out, [s.accountId]: s } : out), {});
};

/**
 * Scores one account now. Free from the signals already stored; {@code fresh} reads its company page first
 * (2 credits, only when there is a page). Resolves the score item, or null when the server could not score it.
 */
export const scoreOne = async (accountId, fresh = false) => {
  const res = await makeApi({ url: URLS.signalsScores, method: "POST", data: { accountIds: [accountId], freshRead: fresh } });
  const scores = (ok(res) && res.data && res.data.scores) || [];
  return scores.find(x => x && x.accountId === accountId) || null;
};

export const SCORES_UPDATED = "sg:scores-updated";
const JOB_POLL_MS = 3000;

/**
 * Scores many accounts from their stored signals (free): `ids`, or every account when null. Up to 50 answer at
 * once; more run as a job this waits on. Resolves {accountId: score item}, or null when the server could not. Tells
 * the page (SCORES_UPDATED) so rows on screen re-read their scores.
 */
export const scoreMany = async (ids, extra = {}) => {
  const data = ids && ids.length ? { accountIds: ids } : { ...extra };
  let res = await makeApi({ url: URLS.signalsScores, method: "POST", data });
  if (!ok(res) || !res.data) return null;
  let body = res.data;
  if (body.jobId) {
    for (;;) {
      await new Promise(r => setTimeout(r, JOB_POLL_MS)); // eslint-disable-line no-await-in-loop
      res = await makeApi({ url: `${URLS.signalsScores}/jobs/${body.jobId}`, method: "GET" }); // eslint-disable-line no-await-in-loop
      if (!ok(res) || !res.data) return null;
      if (res.data.status === "failed") return null;
      if (res.data.status === "done") {
        body = res.data.result || {};
        break;
      }
    }
  }
  const out = (body.scores || []).reduce((acc, x) => (x && x.accountId ? { ...acc, [x.accountId]: x } : acc), {});
  window.dispatchEvent(new CustomEvent(SCORES_UPDATED, { detail: out }));
  return out;
};

/** The team's hottest accounts (latest score each, best first), or null on failure. */
export const fetchHot = async (limit = 5) => {
  const res = await makeApi({ url: URLS.signalsScores, method: "GET", params: { limit } });
  return ok(res) && res.data ? res.data.scores || [] : null;
};

const GAP = 6;
// A change smaller than this is "steady", not an arrow (the server's MOVE_MIN).
export const MOVE_MIN = 3;
const isCompetitor = item => !!item && item.score == null && item.reason === "competitor";

/** "up" / "down" when the score moved at least MOVE_MIN points, "steady" when less, "" without a previous score. */
export const movementOf = item => {
  const { score, previousScore, movement } = item || {};
  if (movement) return movement;
  if (score == null || previousScore == null) return "";
  const d = score - previousScore;
  return d >= MOVE_MIN ? "up" : d <= -MOVE_MIN ? "down" : "steady";
};

const ScorePopover = ({ anchor, item, onClose, onEnter, onLeave, accountId, onScored }) => {
  const ref = useRef(null);
  const [reading, setReading] = useState(false);
  // Nothing stored to score from: read their page now, once, at the cost the button says.
  const readNow = async () => {
    setReading(true);
    try {
      const got = await scoreOne(accountId, true);
      if (got && onScored) onScored(got);
    } finally {
      setReading(false);
    }
  };
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el || !anchor) return;
      const r = anchor.getBoundingClientRect();
      el.style.setProperty("--as-pop-top", `${r.bottom + GAP}px`);
      el.style.setProperty("--as-pop-left", `${Math.max(8, Math.min(r.left, window.innerWidth - el.offsetWidth - 8))}px`);
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor]);
  useEffect(() => {
    const onKey = e => e.key === "Escape" && onClose();
    const onDown = e => ref.current && !ref.current.contains(e.target) && !(anchor && anchor.contains(e.target)) && onClose();
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [anchor, onClose]);
  const reasons = (item.reasons || []).slice(0, 3);
  const when = timeAgo(item.computedAt);
  return ReactDOM.createPortal(
    <div ref={ref} className="as-pop" role="dialog" aria-label="Why this score" onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={e => e.stopPropagation()}>
      <div className="as-pop__head">
        {isCompetitor(item) ? "Your competitor" : item.score == null ? "Not enough signals yet" : `Score ${item.score}${moveText(item)}`}
      </div>
      {isCompetitor(item) ? (
        <p className="as-pop__quiet">
          We don't score your competitors. See what they're building on <Link to="/signals/competitors">Your competitors</Link>.
        </p>
      ) : item.score == null ? (
        <>
          <p className="as-pop__quiet">Nothing in the last 30 days to read yet: no hiring, posts, replies, opens or moves.</p>
          {accountId && onScored && (
            <button type="button" className="sg-btn sg-btn--small sg-btn--ghost as-pop__act" disabled={reading} onClick={readNow}>
              {reading ? "Reading their page…" : "Read their page now · 2 credits"}
            </button>
          )}
        </>
      ) : (
        <ul className="as-pop__reasons">{reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
      )}
      {(item.sellerProfileName || when) && (
        <div className="as-pop__foot">
          {[item.sellerProfileName ? `Scored for ${item.sellerProfileName}` : "Scored", when].filter(Boolean).join(" · ")}
        </div>
      )}
      {!isCompetitor(item) && (
        <div className="as-pop__foot">
          Scored once. <Link to="/agents/templates" onClick={onClose}>Keep it scored weekly</Link> with the Account scorer agent.
        </div>
      )}
    </div>,
    document.body
  );
};

/** ", up from 62" / ", down from 90" / ", steady" (under MOVE_MIN points) / "" */
export const moveText = item => {
  const m = movementOf(item);
  if (!m) return "";
  return m === "steady" ? ", steady" : `, ${m} from ${item.previousScore}`;
};

/** The score as a pill with its move since last time; hover or click shows why. Clicks never reach the row. */
export const ScorePill = ({ item, accountId, onScored }) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [scoring, setScoring] = useState(false);
  const anchor = useRef(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  // Not scored yet: score it now from what Signals already has (free); the popover offers the paid read after.
  const scoreNow = async e => {
    e.stopPropagation();
    setScoring(true);
    try {
      const got = await scoreOne(accountId);
      if (onScored) onScored(got || { accountId, score: null, reason: "not_enough_signals" });
    } finally {
      setScoring(false);
    }
  };
  if (!item) {
    if (!accountId || !onScored) return <span className="as-none">--</span>;
    return (
      <button type="button" className="as-cta" disabled={scoring} title="Free. Scores from the signals already recorded here." onClick={scoreNow}>
        {scoring ? "Scoring…" : "Score"}
      </button>
    );
  }
  const { score } = item;
  const m = movementOf(item);
  const dir = m === "up" || m === "down" ? m : "";
  const rival = isCompetitor(item);
  const tone = rival ? "rival" : score == null ? "quiet" : score >= 70 ? "hot" : score >= 40 ? "warm" : "cool";
  const label = rival ? "Competitor" : score == null ? "Not enough signals yet" : `Score ${score}${moveText(item)}`;
  const enter = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => { if (!pinned) timer.current = setTimeout(() => setOpen(false), 150); };
  const close = () => { setOpen(false); setPinned(false); };
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={`as-pill as-pill--${tone}`}
        aria-expanded={open}
        aria-label={label}
        onClick={e => { e.stopPropagation(); if (pinned) close(); else { setPinned(true); setOpen(true); } }}
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        {rival ? "Competitor" : score == null ? "Not enough signals yet" : score}
        {dir && <i className={`material-icons-outlined as-pill__arrow as-pill__arrow--${dir}`}>{dir === "up" ? "arrow_upward" : "arrow_downward"}</i>}
      </button>
      {open && <ScorePopover anchor={anchor.current} item={item} onClose={close} onEnter={enter} onLeave={leave} accountId={accountId} onScored={onScored} />}
    </>
  );
};

/** My signals: the five accounts scoring highest right now, each with its strongest reason. */
/** The top-scored accounts. `bare`: rows only, for a frame that brings its own title (the Home widget). */
export const HotAccounts = ({ bare = false }) => {
  const [rows, setRows] = useState(undefined);
  useEffect(() => {
    let live = true;
    fetchHot(5).then(r => live && setRows(r));
    return () => { live = false; };
  }, []);
  if (rows === null) return bare ? <p className="as-hot__empty">Couldn't load scores. Try again in a moment.</p> : null;
  return (
    <div className={`as-hot ${bare ? "as-hot--bare" : ""}`}>
      {!bare && (
        <div className="as-hot__head">
          <h3>Hot accounts</h3>
          <span className="as-hot__sub">Scored from the last 30 days of signals</span>
        </div>
      )}
      {rows === undefined ? (
        <div className="as-hot__skel" />
      ) : !rows.length ? (
        <p className="as-hot__empty">
          Scores show here once your accounts are scored, for example by the <Link to="/agents/templates">Account scorer</Link> agent.
        </p>
      ) : (
        rows.map(s => (
          <div key={s.accountId} className="as-hot__row">
            <AccountLogo domain={s.domain} name={s.name || s.domain || "?"} />
            <span className="as-hot__text">
              <Link className="as-hot__name" to={`/accounts/${s.accountId}/overview`}>{s.name || s.domain}</Link>
              {(s.reasons || [])[0] && <span className="as-hot__why">{s.reasons[0]}</span>}
            </span>
            <ScorePill item={s} />
          </div>
        ))
      )}
    </div>
  );
};
